/**
 * Winhouse Tutors — Google Analytics 4 (GA4) integration.
 *
 * Single source of truth for GA4. Loads `gtag.js` exactly once, then exposes:
 *  - initGA()          → boots gtag.js + `window.gtag`, safe to call many times
 *  - trackPageView()   → SPA-friendly page_view (fired on every route change)
 *  - trackEvent()      → generic event helper
 *  - Named helpers for the nine Winhouse custom events:
 *      trackNavigationClick, trackBookTrialLesson, trackStudentEnquiry,
 *      trackTutorApplication, trackPricingCtaClick, trackWhatsAppClick,
 *      trackNewsletterSignup, trackStudyAbroadEnquiry, trackResourceDownload
 *
 * NOTE: This is the ONLY place that injects the gtag script. Do not add
 * gtag snippets to index.html or other components — that would cause
 * double page_view firing.
 *
 * The measurement ID is a public identifier (shipped to browsers by design),
 * so a constant is acceptable here.
 */

export const GA4_MEASUREMENT_ID = 'G-TQMHNPRTMM';

let gaInitialized = false;

/**
 * Load gtag.js once and configure the GA4 property. Safe to call repeatedly.
 * We disable the automatic page_view so the React Router listener is the
 * single source of `page_view` events (prevents duplicates on SPA nav).
 * Enhanced Measurement (scrolls, outbound clicks, etc.) continues to work
 * because it is controlled by the GA4 property, not gtag config flags.
 */
export const initGA = () => {
  if (gaInitialized || typeof window === 'undefined') return;

  // 1. Create dataLayer + gtag stub.
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // Use arguments (not rest) so gtag() calls remain equivalent to the
    // canonical Google snippet, matching what Google's parser expects.
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: false, // SPA router fires page_view manually.
  });

  // 2. Inject the loader script exactly once. Guarded by data-attr so hot
  // reload / re-renders never insert a second tag.
  const existing = document.querySelector('script[data-ga4="winhouse"]');
  if (!existing) {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    s.setAttribute('data-ga4', 'winhouse');
    document.head.appendChild(s);
  }

  gaInitialized = true;
};

/**
 * Fire a GA4 `page_view` for the current SPA location.
 * Called from <Analytics /> on every react-router location change.
 */
export const trackPageView = (path, title) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title || document.title,
  });
};

/**
 * Generic GA4 event dispatcher. All named helpers below funnel through this.
 * Silent no-op if gtag hasn't finished loading yet — analytics must never
 * break the UX.
 */
export const trackEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', eventName, params);
  } catch {
    /* noop */
  }
};

/* -------------------------------------------------------------------------- */
/*  Winhouse — nine canonical custom events                                    */
/* -------------------------------------------------------------------------- */

export const trackNavigationClick = (label, destination) =>
  trackEvent('navigation_click', {
    link_label: label,
    link_destination: destination,
    source_page: typeof window !== 'undefined' ? window.location.pathname : '',
  });

export const trackBookTrialLesson = (source) =>
  trackEvent('book_trial_lesson', {
    source: source || 'unknown',
    source_page: typeof window !== 'undefined' ? window.location.pathname : '',
  });

export const trackStudentEnquiry = (data = {}) =>
  trackEvent('student_enquiry', {
    program_type: data.program_type || '',
    subject: data.subject || '',
    year_level: data.year_level || '',
    preferred_format: data.preferred_format || '',
  });

export const trackTutorApplication = (data = {}) =>
  trackEvent('tutor_application', {
    subjects: data.subjects || '',
    has_cv: !!data.has_cv,
  });

export const trackPricingCtaClick = (service, planId) =>
  trackEvent('pricing_cta_click', {
    service: service || '',
    plan_id: planId || '',
  });

export const trackWhatsAppClick = (source, variant = 'default') =>
  trackEvent('whatsapp_click', {
    source: source || 'unknown',
    variant,
    source_page: typeof window !== 'undefined' ? window.location.pathname : '',
  });

export const trackNewsletterSignup = (resourceName, category) =>
  trackEvent('newsletter_signup', {
    resource_name: resourceName || '',
    category: category || '',
  });

export const trackStudyAbroadEnquiry = (hubSlug, ctaLabel) =>
  trackEvent('study_abroad_enquiry', {
    hub_slug: hubSlug || '',
    cta_label: ctaLabel || '',
  });

export const trackResourceDownload = (resourceTitle, category) =>
  trackEvent('resource_download', {
    resource_title: resourceTitle || '',
    category: category || '',
  });
